import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { createKubernetesStatusColumn } from '@internal/backstage-plugin-compute-resource-discovery';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { OwnershipGroupsDropdownFieldExtension } from './components/scaffolder/OwnershipGroupsDropdown';
import { TeamMembersSelectorFieldExtension } from './components/scaffolder/TeamMembersSelector';
import { GithubUserSelectorFieldExtension } from './components/scaffolder/GithubUserSelector';
import { RepositoryNameDropdownFieldExtension } from './components/scaffolder/RepositoryNameDropdown';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';

import {
  AlertDisplay,
  GitHubIcon,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import { customTheme } from './theme/customTheme';
// import { GithubTeamsPage } from '@internal/backstage-plugin-github-teams';
// import { GroupManagerPage } from '@internal/backstage-plugin-group-manager';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { configApiRef, useApi } from '@backstage/core-plugin-api';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'gdp-theme',
      title: 'GDP Labs',
      variant: 'dark',
      icon: <GitHubIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customTheme} children={children} />
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const config = useApi(configApiRef);
      const isCI = config.getOptionalBoolean('e2eTest.CI') ?? false;
      return (
        <SignInPage
          {...props}
          auto
          title="Select a sign-in method"
          providers={[
            ...(isCI ? (['guest'] as const) : []),
            {
              id: 'github-auth-provider',
              title: 'GitHub',
              message: 'Sign in using GitHub',
              apiRef: githubAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

// Custom columns function - adds Kubernetes status column for system kind
const myColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const kind = entityListContext.filters.kind?.value;

  if (kind === 'system') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createSpecLifecycleColumn(),
      createKubernetesStatusColumn(),
    ];
  }

  if (kind === 'component') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createSystemColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createSpecTypeColumn(),
      CatalogTable.columns.createSpecLifecycleColumn(),
    ];
  }

  if (kind === 'group') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createSpecTypeColumn(),
      CatalogTable.columns.createTagsColumn(),
    ];
  }

  return CatalogTable.defaultColumnsFunc(entityListContext);
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={myColumnsFunc}
          initialKind="system"
          initiallySelectedFilter="all"
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{
            title: 'Create or Delete Project',
            subtitle:
              'Create a new project/environment or delete existing project using the templates provided.',
          }}
          groups={[
            {
              title: 'New Project',
              filter: entity => entity?.spec?.type === 'project-template',
            },
            {
              title: 'New Environment',
              filter: entity => entity?.spec?.type === 'environment-template',
            },
            {
              title: 'Delete',
              filter: entity => entity?.spec?.type === 'delete-template',
            },
          ]}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <OwnershipGroupsDropdownFieldExtension />
        <TeamMembersSelectorFieldExtension />
        <GithubUserSelectorFieldExtension />
        <RepositoryNameDropdownFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    {/* <Route path="/github-teams" element={<GithubTeamsPage />} /> */}
    {/* <Route path="/group-manager" element={<GroupManagerPage />} /> */}
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
